import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebaseConfig.js';
import Layout from '../Layout';
import Loader from '../Loader';
import Main from '../Main';
import Quiz from '../Quiz';
import Result from '../Result';
import SEO from '../SEO';
import { shuffle } from '../../utils';
import { saveQuizResult } from '../../utils/quizResults';

const MakeYourOwnTest = () => {
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(null);
  const [data, setData] = useState(null);
  const [countdownTime, setCountdownTime] = useState(null);
  const [isQuizStarted, setIsQuizStarted] = useState(false);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [user, setUser] = useState(null);
  const [quizMetadata, setQuizMetadata] = useState({ testType: 'custom', testName: 'Custom Quiz' });

  // Listen for auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const startQuiz = (data, countdownTime, metadata = {}) => {
    setLoading(true);
    setLoadingMessage({
      title: 'Loading your quiz...',
      message: "It won't be long!",
    });
    setCountdownTime(countdownTime);
    setQuizMetadata({
      testType: metadata.testType || 'custom',
      testName: metadata.testName || metadata.category || 'Custom Quiz'
    });

    setTimeout(() => {
      setData(data);
      setIsQuizStarted(true);
      setLoading(false);
    }, 1000);
  };

  const endQuiz = async (resultData) => {
    setLoading(true);
    setLoadingMessage({
      title: 'Saving your results...',
      message: 'Just a moment!',
    });

    // Save to database if user is logged in
    if (user) {
      await saveQuizResult(
        user.uid,
        resultData,
        quizMetadata.testType,
        quizMetadata.testName
      );
    }

    setTimeout(() => {
      setIsQuizStarted(false);
      setIsQuizCompleted(true);
      setResultData(resultData);
      setLoading(false);
    }, 2000);
  };

  const replayQuiz = () => {
    setLoading(true);
    setLoadingMessage({
      title: 'Getting ready for round two.',
      message: "It won't take long!",
    });

    const shuffledData = shuffle(data);
    shuffledData.forEach(element => {
      element.options = shuffle(element.options);
    });

    setData(shuffledData);

    setTimeout(() => {
      setIsQuizStarted(true);
      setIsQuizCompleted(false);
      setResultData(null);
      setLoading(false);
    }, 1000);
  };

  const resetQuiz = () => {
    setLoading(true);
    setLoadingMessage({
      title: 'Loading the home screen.',
      message: 'Thank you for playing!',
    });

    setTimeout(() => {
      setData(null);
      setCountdownTime(null);
      setIsQuizStarted(false);
      setIsQuizCompleted(false);
      setResultData(null);
      setLoading(false);
    }, 1000);
  };

  // Hide header only during active quiz taking
  const hideHeader = isQuizStarted && !isQuizCompleted;

  return (
    <>
      <SEO
        title="Create Custom NUST Mock Tests | Prepify - NET Test Builder"
        description="Create your own custom NUST Entry Test (NET) mock exams with Prepify. Choose categories, difficulty levels, and time limits. Perfect for NET Engineering, CS, and Business preparation."
        keywords="Create NUST Mock Test, Custom NET Test, NUST Test Builder, NET Practice Test Creator, Custom Entrance Exam, NUST Preparation Tool"
        url="/make-your-own-test"
      />
      <Layout hideHeaderOnQuiz={hideHeader}>
        {loading && <Loader {...loadingMessage} />}
        {!loading && !isQuizStarted && !isQuizCompleted && (
          <Main startQuiz={startQuiz} />
        )}
        {!loading && isQuizStarted && (
          <Quiz data={data} countdownTime={countdownTime} endQuiz={endQuiz} />
        )}
        {!loading && isQuizCompleted && (
          <Result {...resultData} replayQuiz={replayQuiz} resetQuiz={resetQuiz} />
        )}
      </Layout>
    </>
  );
};

export default MakeYourOwnTest;
